import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, X, Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { signInRetailer, RetailerUser } from '../services/authService';

interface VerificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLoginSuccess: (retailer: RetailerUser) => void;
}

const VerificationModal: React.FC<VerificationModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const retailer = await signInRetailer(email, password);
      onLoginSuccess(retailer);
      setEmail('');
      setPassword('');
    } catch (err: any) {
      const code = err?.code || '';
      if (code === 'auth/user-not-found' || code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        setError('Invalid email or password. Please try again.');
      } else if (code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else {
        setError(err.message || 'Sign in failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyClick = () => {
    onClose();
    navigate('/apply');
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-brand-dark/90 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="bg-brand-light-bg p-6 border-b border-gray-100 flex justify-between items-center">
          <div className="flex items-center space-x-2 text-brand-red">
            <ShieldCheck className="w-5 h-5" />
            <span className="font-bold uppercase tracking-widest text-xs">Wholesale Access</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-8">
          <h2 className="text-2xl font-display font-bold text-brand-dark mb-2">Retailer Sign In</h2>
          <p className="text-sm text-brand-text-muted mb-6">
            Sign in with your approved retailer account to view wholesale pricing and place orders.
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2 mb-4">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase text-brand-text-muted mb-1.5">Email Address</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-gray-200 rounded-[8px] px-4 py-3 text-sm text-brand-dark placeholder:text-brand-text-muted/50 focus:ring-2 focus:ring-brand-red focus:border-brand-red outline-none transition-all"
                placeholder="you@yourbusiness.ca"
                disabled={isLoading}
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase text-brand-text-muted mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-gray-200 rounded-[8px] px-4 py-3 pr-11 text-sm text-brand-dark placeholder:text-brand-text-muted/50 focus:ring-2 focus:ring-brand-red focus:border-brand-red outline-none transition-all"
                  placeholder="Enter your password"
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-text-muted/60 hover:text-brand-dark transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-brand-red text-white font-semibold text-sm py-3.5 rounded-[8px] hover:bg-brand-red-dark transition-all disabled:opacity-60 flex items-center justify-center gap-2 mt-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Signing In...
                </>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          <div className="mt-6 pt-5 border-t border-gray-100 text-center">
            <p className="text-sm text-brand-text-muted">
              Don't have an account?{' '}
              <button
                onClick={handleApplyClick}
                className="font-semibold text-brand-red hover:text-brand-red-dark transition-colors"
              >
                Apply for Wholesale
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationModal;
